import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Chip,
  Badge,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
  Divider,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Search,
  Send,
  Star,
  StarOff,
  Archive,
  Clock,
  Filter,
  MessageSquare,
  User,
  Building2,
  Briefcase,
  Target,
  Tag,
} from "lucide-react";
import {
  fetchLIConversations,
  fetchLIConversation,
  sendLIReply,
  updateLIConversation,
  fetchLIAccounts,
} from "../../../../../../redux/actions/linkedinOutreachAction";

const REFRESH_INTERVAL = 30000;

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const AvatarCircle = ({ name, size = 40 }) => {
  const theme = useTheme();
  const letter = (name || "?").charAt(0).toUpperCase();
  const colors = [
    "#3b82f6",
    "#8b5cf6",
    "#ec4899",
    "#f59e0b",
    "#10b981",
    "#06b6d4",
    "#ef4444",
  ];
  const colorIdx =
    name
      ? name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % colors.length
      : 0;
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: "50%",
        bgcolor: alpha(colors[colorIdx], 0.15),
        color: colors[colorIdx],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size * 0.42,
        flexShrink: 0,
      }}
    >
      {letter}
    </Box>
  );
};

const UnifiedInbox = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    conversations,
    currentConversation,
    messages,
    accounts,
    loading,
  } = useSelector((state) => state.linkedinOutreach);

  const [searchQuery, setSearchQuery] = useState("");
  const [filterAccount, setFilterAccount] = useState("");
  const [filterUnread, setFilterUnread] = useState(false);
  const [filterStarred, setFilterStarred] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const messagesEndRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  // Fetch accounts on mount
  useEffect(() => {
    dispatch(fetchLIAccounts());
  }, [dispatch]);

  // Fetch conversations
  const loadConversations = useCallback(() => {
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (filterAccount) params.account_id = filterAccount;
    if (filterUnread) params.is_unread = true;
    if (filterStarred) params.is_starred = true;
    dispatch(fetchLIConversations(params));
  }, [dispatch, searchQuery, filterAccount, filterUnread, filterStarred]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Auto-refresh
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      loadConversations();
      if (selectedId) {
        dispatch(fetchLIConversation(selectedId));
      }
    }, REFRESH_INTERVAL);
    return () => clearInterval(refreshIntervalRef.current);
  }, [loadConversations, selectedId, dispatch]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const conversationItems = conversations?.items || [];

  const handleSelectConversation = (conv) => {
    setSelectedId(conv.id);
    dispatch(fetchLIConversation(conv.id));
    setReplyText("");
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedId) return;
    setSending(true);
    try {
      await dispatch(sendLIReply(selectedId, replyText.trim()));
      setReplyText("");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handleStar = async (e, conv) => {
    e.stopPropagation();
    await dispatch(
      updateLIConversation(conv.id, { is_starred: !conv.is_starred })
    );
  };

  const handleArchive = async () => {
    if (!selectedId) return;
    await dispatch(updateLIConversation(selectedId, { is_archived: true }));
    setSelectedId(null);
    loadConversations();
  };

  const handleSnooze = async () => {
    if (!selectedId) return;
    const snoozeUntil = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    await dispatch(
      updateLIConversation(selectedId, { snoozed_until: snoozeUntil })
    );
    loadConversations();
  };

  const conv = currentConversation;
  const lead = conv?.lead || null;
  const msgList = messages || [];

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        bgcolor: theme.palette.background.paper,
      }}
    >
      {/* ════════════ Left column: conversation list ════════════ */}
      <Box
        sx={{
          width: 300,
          flexShrink: 0,
          borderRight: `1px solid ${theme.palette.divider}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Search + filters */}
        <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={16} color={theme.palette.text.disabled} />
                </InputAdornment>
              ),
              sx: { borderRadius: "8px", fontSize: "0.85rem" },
            }}
          />
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 100, flex: 1 }}>
              <Select
                value={filterAccount}
                onChange={(e) => setFilterAccount(e.target.value)}
                displayEmpty
                sx={{ borderRadius: "8px", fontSize: "0.78rem" }}
              >
                <MenuItem value="">All Accounts</MenuItem>
                {(accounts || []).map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.linkedin_email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Unread only">
              <IconButton
                size="small"
                onClick={() => setFilterUnread(!filterUnread)}
                sx={{
                  borderRadius: "8px",
                  bgcolor: filterUnread
                    ? alpha(theme.palette.primary.main, 0.1)
                    : "transparent",
                  border: `1px solid ${
                    filterUnread
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                }}
              >
                <MessageSquare
                  size={14}
                  color={
                    filterUnread
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary
                  }
                />
              </IconButton>
            </Tooltip>
            <Tooltip title="Starred only">
              <IconButton
                size="small"
                onClick={() => setFilterStarred(!filterStarred)}
                sx={{
                  borderRadius: "8px",
                  bgcolor: filterStarred
                    ? alpha("#f59e0b", 0.1)
                    : "transparent",
                  border: `1px solid ${
                    filterStarred ? "#f59e0b" : theme.palette.divider
                  }`,
                }}
              >
                <Star
                  size={14}
                  color={
                    filterStarred ? "#f59e0b" : theme.palette.text.secondary
                  }
                />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Divider />

        {/* Conversation list */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {loading && conversationItems.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : conversationItems.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 4,
                px: 2,
                gap: 1,
              }}
            >
              <MessageSquare
                size={24}
                color={theme.palette.text.disabled}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: "center",
                }}
              >
                No conversations found.
              </Typography>
            </Box>
          ) : (
            conversationItems.map((item) => {
              const isSelected = selectedId === item.id;
              return (
                <Box
                  key={item.id}
                  onClick={() => handleSelectConversation(item)}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    px: 1.5,
                    py: 1.25,
                    cursor: "pointer",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, 0.06)
                      : "transparent",
                    borderLeft: isSelected
                      ? `3px solid ${theme.palette.primary.main}`
                      : "3px solid transparent",
                    transition: "all 0.15s",
                    "&:hover": {
                      bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, 0.08)
                        : alpha(theme.palette.action.hover, 0.5),
                    },
                  }}
                >
                  <AvatarCircle
                    name={item.participant_name}
                    size={36}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: item.is_unread ? 700 : 500,
                          fontSize: "0.82rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 140,
                        }}
                      >
                        {item.participant_name || "Unknown"}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {item.is_unread && (
                          <Box
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              bgcolor: theme.palette.primary.main,
                            }}
                          />
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.disabled,
                            fontSize: "0.68rem",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {timeAgo(item.last_message_at)}
                        </Typography>
                      </Box>
                    </Box>
                    {item.headline && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.text.secondary,
                          display: "block",
                          fontSize: "0.7rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.3,
                        }}
                      >
                        {item.headline}
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.disabled,
                        display: "block",
                        fontSize: "0.72rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        mt: 0.25,
                      }}
                    >
                      {item.last_message_preview || "No messages yet"}
                    </Typography>
                    {item.account_email && (
                      <Chip
                        label={item.account_email}
                        size="small"
                        variant="outlined"
                        sx={{
                          mt: 0.5,
                          height: 18,
                          fontSize: "0.6rem",
                          borderRadius: "4px",
                          borderColor: alpha(theme.palette.text.disabled, 0.3),
                          color: theme.palette.text.disabled,
                        }}
                      />
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleStar(e, item)}
                    sx={{ alignSelf: "flex-start", mt: -0.25 }}
                  >
                    {item.is_starred ? (
                      <Star size={14} fill="#f59e0b" color="#f59e0b" />
                    ) : (
                      <StarOff
                        size={14}
                        color={theme.palette.text.disabled}
                      />
                    )}
                  </IconButton>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* ════════════ Center column: messages ════════════ */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minWidth: 0,
        }}
      >
        {!selectedId ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
            }}
          >
            <MessageSquare
              size={36}
              color={theme.palette.text.disabled}
            />
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary }}
            >
              Select a conversation to view messages
            </Typography>
          </Box>
        ) : (
          <>
            {/* Conversation header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor:
                  theme.palette.mode === "dark"
                    ? alpha("#fff", 0.02)
                    : alpha("#000", 0.01),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <AvatarCircle
                  name={conv?.participant_name}
                  size={32}
                />
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: "0.88rem" }}
                  >
                    {conv?.participant_name || "Unknown"}
                  </Typography>
                  {conv?.headline && (
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {conv.headline}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Tooltip title="Star">
                  <IconButton
                    size="small"
                    onClick={(e) => conv && handleStar(e, conv)}
                  >
                    {conv?.is_starred ? (
                      <Star size={16} fill="#f59e0b" color="#f59e0b" />
                    ) : (
                      <Star size={16} color={theme.palette.text.secondary} />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Snooze 24h">
                  <IconButton size="small" onClick={handleSnooze}>
                    <Clock size={16} color={theme.palette.text.secondary} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Archive">
                  <IconButton size="small" onClick={handleArchive}>
                    <Archive
                      size={16}
                      color={theme.palette.text.secondary}
                    />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                px: 2,
                py: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {msgList.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.disabled }}
                  >
                    No messages in this conversation.
                  </Typography>
                </Box>
              ) : (
                msgList.map((msg) => {
                  const isOutbound = msg.direction === "outbound";
                  return (
                    <Box
                      key={msg.id}
                      sx={{
                        display: "flex",
                        justifyContent: isOutbound
                          ? "flex-end"
                          : "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "70%",
                          px: 1.75,
                          py: 1,
                          borderRadius: isOutbound
                            ? "14px 14px 4px 14px"
                            : "14px 14px 14px 4px",
                          bgcolor: isOutbound
                            ? theme.palette.primary.main
                            : theme.palette.mode === "dark"
                            ? alpha("#fff", 0.06)
                            : alpha("#000", 0.05),
                          color: isOutbound
                            ? "#fff"
                            : theme.palette.text.primary,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {msg.body}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            textAlign: "right",
                            mt: 0.5,
                            fontSize: "0.65rem",
                            opacity: 0.7,
                          }}
                        >
                          {timeAgo(msg.created_at)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Reply input */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
              }}
            >
              <TextField
                placeholder="Type a reply..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                fullWidth
                multiline
                maxRows={4}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    fontSize: "0.85rem",
                  },
                }}
              />
              <IconButton
                onClick={handleSendReply}
                disabled={!replyText.trim() || sending}
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                  borderRadius: "10px",
                  width: 38,
                  height: 38,
                  "&:hover": { bgcolor: theme.palette.primary.dark },
                  "&.Mui-disabled": {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {sending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send size={16} />
                )}
              </IconButton>
            </Box>
          </>
        )}
      </Box>

      {/* ════════════ Right column: lead/participant context ════════════ */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          borderLeft: `1px solid ${theme.palette.divider}`,
          overflow: "auto",
          display: { xs: "none", lg: "flex" },
          flexDirection: "column",
        }}
      >
        {!selectedId || !conv ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: theme.palette.text.disabled }}
            >
              Select a conversation
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Profile header */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                pt: 1,
              }}
            >
              <AvatarCircle
                name={lead?.full_name || conv.participant_name}
                size={56}
              />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, textAlign: "center" }}
              >
                {lead?.full_name || conv.participant_name || "Unknown"}
              </Typography>
              {(lead?.headline || conv.headline) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                    lineHeight: 1.3,
                  }}
                >
                  {lead?.headline || conv.headline}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Context info */}
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
            >
              {(lead?.company || conv.company) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Building2
                    size={14}
                    color={theme.palette.text.secondary}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {lead?.company || conv.company}
                  </Typography>
                </Box>
              )}
              {(lead?.job_title || conv.job_title) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Briefcase
                    size={14}
                    color={theme.palette.text.secondary}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {lead?.job_title || conv.job_title}
                  </Typography>
                </Box>
              )}
              {lead?.location && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <User
                    size={14}
                    color={theme.palette.text.secondary}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    {lead.location}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Lead-specific info */}
            {lead && (
              <>
                <Divider />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.65rem",
                    }}
                  >
                    Lead Details
                  </Typography>

                  {lead.validation_status && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Validation
                      </Typography>
                      <Chip
                        label={lead.validation_status}
                        size="small"
                        color={
                          lead.validation_status === "validated"
                            ? "success"
                            : lead.validation_status === "rejected"
                            ? "error"
                            : "default"
                        }
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.65rem",
                          height: 20,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </Box>
                  )}

                  {lead.status && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        Lead Status
                      </Typography>
                      <Chip
                        label={lead.status}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderRadius: "6px",
                          fontSize: "0.65rem",
                          height: 20,
                          textTransform: "capitalize",
                        }}
                      />
                    </Box>
                  )}

                  {lead.icp_name && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Target
                          size={12}
                          color={theme.palette.text.secondary}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          ICP
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500 }}
                      >
                        {lead.icp_name}
                      </Typography>
                    </Box>
                  )}

                  {lead.icp_score != null && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        ICP Score
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600 }}
                      >
                        {lead.icp_score}/100
                      </Typography>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {/* Account info */}
            {conv.account_email && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.text.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.65rem",
                      mb: 0.75,
                      display: "block",
                    }}
                  >
                    Account
                  </Typography>
                  <Chip
                    label={conv.account_email}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: "6px",
                      fontSize: "0.68rem",
                      height: 22,
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UnifiedInbox;
